<template>
    <Skeleton v-if="loader.pending.value" :items="['block--height-8rem', 'block--height-8rem', 'block--height-8rem']"/>
    <div v-else>
        <div
            v-for="coordsGroup in coordsGroupedByJobsite">
            <RichResponseListItem
                class="demand-list-item--jobsite"
                iconType="image"
                :icon="getImageForJobsite(coordsGroup.jobsite)"
                :title="coordsGroup.jobsite?.name"
                :button="true"
                @click="onClickButtonMoreInfo(coordsGroup.jobsite)"
                >
                {{ coordsGroup.jobsite ? getShortJobsiteDescription(coordsGroup.jobsite, true, ' - ') : '' }}

            </RichResponseListItem>

            <RichResponseListItem
                class="demand-list-item--demand"
                v-for="item in groupByDate(coordsGroup)"
                :title="date.formatKapitelDate(item.date, 'EEE, d. MMMM yyyy')"
            >
                <ion-chip
                    class="small"
                    :color="shift.inesIsConsidering ? 'success' : 'primary'"
                    v-for="shift in item.shifts"
                >
                    {{ shift.shiftType }}
                </ion-chip>
                <template v-slot:end>
                    <IonButton
                        color="secondary"
                        @click="onClickAvailabilitySuggestion(coordsGroup.jobsite, item.date)"
                    >
                        <ion-icon
                            v-if="item.allAvailable"
                            slot="start"
                            src="/icons/check.svg" />
                        <ion-icon
                            v-else
                            slot="start"
                            src="/icons/plus.svg" />
                        {{ item.btnLabel }}
                    </IonButton>
                </template>
            </RichResponseListItem>

        </div>


        <JobsiteDemand
            v-model="jobsiteDemandDetailModelValue"
        />

        <JobsiteDemandDayDetail v-if="selectedJobsite && selectedDate" :jobsite="selectedJobsite" :date="selectedDate" v-model="dayDetailOpen" />

    </div>
</template>

<script setup lang="ts">
import RichResponseListItem from "./components/RichResponseListItem.vue"
import date from "@/helper/datetime/date"
import {getImageForJobsite, getShortJobsiteDescription} from '@/helper/jobsite'
import {computed, Ref, ref, watch} from "vue"
import {DemandJoDaSh, Jobsite, ShiftType} from "@/graphql/generated/graphql"
import {DataLoader} from "@/helper/dataLoader.js";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import {fetchDemandForDate} from "@/helper/demandBrowser";
import JobsiteDemand from "@/views/JobsiteDemand.vue";
import JobsiteDemandDayDetail from "@/views/JobsiteDemandDayDetail.vue";
import {IonButton, IonChip, IonIcon} from "@ionic/vue";
import {groupBy} from "@/helper/array";
import Skeleton from "@/components/Skeleton.vue";

type AvailabilityCoordinatesGroup = {
  jobsite: Jobsite;
  coords: DemandJoDaSh[];
}


const coordinates: Ref<DemandJoDaSh[]> = ref([])

type StringBooleanMap = {
  [key: string]: boolean;
}

const props = defineProps<{
    initData: any
}>()

const jobsiteDemandDetailModelValue : Ref<Jobsite|undefined> = ref(undefined)
const jobsiteDemandDetailModalOpen : Ref<boolean> = ref(false)

const dayDetailOpen = ref(true)
const selectedDate = ref()
const selectedJobsite = ref()

watch(()=>dayDetailOpen.value, value =>{
    if(!value){
        loader.reload()
    }
})

const availabilityActionIsProcessing = ref<StringBooleanMap>({})

const coordsGroupedByJobsite = computed(() => coordinates.value.reduce((acc: AvailabilityCoordinatesGroup[], val: DemandJoDaSh) => {
    const group = acc.find((gr: AvailabilityCoordinatesGroup) => gr.jobsite?.id === val.jobsite?.id)
    if (group) {
        group.coords.push(val)
    }
    else {
        acc.push({
            jobsite: val.jobsite,
            coords: [val]
        })
    }

    return acc
}, []).map((group: AvailabilityCoordinatesGroup) => {
    group.coords = group.coords.sort((a,b) => date.cmpKapitelDate(a.date, b.date));
    return group
}));

const groupByDate = (group: AvailabilityCoordinatesGroup) => {
    const result : {date: kapitelDateString, shifts: DemandJoDaSh[], btnLabel: string, allAvailable: boolean, instantBookable: boolean}[]= [];
    groupBy(group.coords, (value: DemandJoDaSh) => value.date).forEach(
        (subGroup, date) => {
            const instantBookable = subGroup.some((item) => item.instantBookable)
            result.push({
                date,
                shifts: subGroup,
                btnLabel: instantBookable ? 'übernehmen' : 'verfügbar',
                allAvailable: subGroup.every(i => i.inesIsConsidering),
                instantBookable
            })
        }
    )
    return result.sort((a, b) => date.cmpKapitelDate(a.date, b.date))
}

const onClickButtonMoreInfo = (jobsite: Jobsite|undefined) => {
    if (!jobsite) {
        return
    }

    jobsiteDemandDetailModelValue.value = jobsite
    jobsiteDemandDetailModalOpen.value = true
}

const onClickAvailabilitySuggestion = (jobsite: Jobsite, date: kapitelDateString) => {
    selectedDate.value = date
    selectedJobsite.value = jobsite
    dayDetailOpen.value = true
}

let loader: DataLoader;

const fetchCoordinates = async () => {
    const jobsiteId  = props.initData.jobsiteId
     loader = new DataLoader()

    props.initData.coordinates.forEach((coordinate:{date:kapitelDateString, shift:ShiftType}) => {
        loader.add(() => {
            coordinates.value = []
            return fetchDemandForDate(
                jobsiteId,
                coordinate.date,
                [coordinate.shift]
            ).then((d)=>{
                coordinates.value.push(...d as DemandJoDaSh[])
            })
        })
    })

    loader.load()
}

fetchCoordinates()

</script>

<style lang="scss">
.wish-demand-ion-spinner {
    width: 1.2em;
    height: 1.2em;
    margin-right: 0.5em;
}

.demand-list-item--jobsite {
    margin-bottom: 0;

    &::part(native) {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
    }
}

.demand-list-item--demand {
    margin-top: 0 !important;

    &:not(:last-child) {
        &::part(native) {
            border-radius: 0 !important;
        }
    }

    &:last-child {
        margin-bottom: 1em;
        
        &::part(native) {
            border-top-left-radius: 0 !important;
            border-top-right-radius: 0 !important;
        }
    }
}
</style>
